import { useEffect, useMemo, useState } from "react";
import {
  Link,
  useActionData,
  useLoaderData,
  useNavigation,
} from "@remix-run/react";
import {
  ActionFunctionArgs,
  json,
  LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { useCart } from "../context/cartContext";
import { useFavorites } from "../context/favoriteContext";
import {
  Heart,
  MapPin,
  Minus,
  Plus,
  ShoppingCart,
  Star,
  X,
} from "lucide-react";
import ButtonTheme from "../components/theme/Button";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { serverPublicOrigin } from "../lib/serverPublicOrigin";
import { getSupabaseWithSessionHeaders } from "../lib/supabase_server";
import { PUBLIC_STORE_PROFILES } from "../lib/public_store_profiles";
import {
  isProductMediaImageFileName,
  PRODUCT_MEDIA_BUCKET_NAME,
  productMediaObjectPath,
} from "../lib/supabase";
import { useRecentlyViewed } from "../hooks/useRecentlyViewed";

export async function loader({ params, request }: LoaderFunctionArgs) {
  const { headers, supabase } = await getSupabaseWithSessionHeaders({
    request,
  });
  const productId = params.id;
  const publicOrigin = serverPublicOrigin(request);

  if (!productId) {
    throw new Response("Product ID is required", { status: 400 });
  }
  const {
    data: { user },
  } = await supabase.auth.getUser();
  // Fetch product data
  const { data: product, error: productError } = await supabase
    .from("Products")
    .select("*")
    .eq("id", productId)
    .eq("status", "ACTIVE")
    .single();

  if (productError || !product) {
    throw new Response("Product not found", { status: 404 });
  }

  // Fetch store data (public profile — ACTIVE stores only)
  const { data: store, error: storeError } = await supabase
    .from(PUBLIC_STORE_PROFILES)
    .select("*")
    .eq("id", product.store_id)
    .single();

  if (storeError || !store) {
    throw new Response("Product not found", { status: 404 });
  }

  // Fetch store address data
  const { data: storeAddress, error: addressError } = await supabase
    .from("StoreAddresses")
    .select("*")
    .eq("store_id", product.store_id)
    .single();

  if (addressError) {
    console.error("Error fetching store address:", addressError);
  }

  // Fetch product reviews
  const { data: reviews, error: reviewsError } = await supabase
    .from("ProductReviews")
    .select(`*`)
    .eq("product_id", productId)
    .order("created_at", { ascending: false });

  if (reviewsError) {
    console.error("Error fetching reviews:", reviewsError);
  }

  // Prefer `media_urls` (admin + realtime uploads); fall back to legacy `media` name list.
  const imageUrls: string[] = [];
  const mediaUrls = product.media_urls;
  if (mediaUrls && typeof mediaUrls === "object" && !Array.isArray(mediaUrls)) {
    for (const [fileName, url] of Object.entries(
      mediaUrls as Record<string, unknown>,
    )) {
      if (typeof url !== "string" || !url) continue;
      if (isProductMediaImageFileName(fileName)) {
        imageUrls.push(url);
      }
    }
  }

  if (
    imageUrls.length === 0 &&
    product.media &&
    Array.isArray(product.media) &&
    product.media.length > 0
  ) {
    for (const mediaFile of product.media) {
      if (typeof mediaFile !== "string") continue;
      const { data: imageData } = supabase.storage
        .from(PRODUCT_MEDIA_BUCKET_NAME)
        .getPublicUrl(
          productMediaObjectPath(product.store_id!, productId, mediaFile),
        );
      imageUrls.push(imageData.publicUrl);
    }
  }

  // If no media, add placeholder
  if (imageUrls.length === 0) {
    imageUrls.push("/images/placeholder.svg?height=400&width=400");
  }

  return json(
    {
      product,
      store,
      storeAddress,
      reviews: reviews || [],
      imageUrls,
      user,
      publicOrigin,
    },
    { headers },
  );
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {

  if (!data?.product) {
    return [{ title: "Product not found — BulkBuyPlace" }];
  }
  const name = data.product.name || "Wholesale product";
  const storeName = data.store?.name ? ` from ${data.store.name}` : "";
  const description = data.product.description
    ? String(data.product.description).slice(0, 155)
    : `Buy ${name}${storeName} in bulk on BulkBuyPlace.`;
  const image = data.imageUrls?.[0];
  const title = `${name}${storeName} | BulkBuyPlace`;
  const canonicalUrl = `${data.publicOrigin}/product/${data.product.id}`;

  return [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "product" },
    ...(image ? [{ property: "og:image", content: image }] : []),
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    ...(image ? [{ name: "twitter:image", content: image }] : []),
    { tagName: "link", rel: "canonical", href: canonicalUrl },
  ];
};

export async function action({ request, params }: ActionFunctionArgs) {
  const { supabase } = await getSupabaseWithSessionHeaders({ request });
  const formData = await request.formData();
  const action = formData.get("action");

  if (action === "addReview") {
    const rating = Number(formData.get("rating"));
    const title = (formData.get("title") as string) || null;
    const comment = formData.get("comment") as string;
    const productId = params.id;

    if (!productId) {
      return json({ error: "Product ID is required" }, { status: 400 });
    }

    // Get current user
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      return json(
        { error: "You must be logged in to write a review" },
        { status: 401 },
      );
    }

    // Verify the user has purchased this product
    const { data: userOrders } = await supabase
      .from("Orders")
      .select("id")
      .eq("customer_id", user.id);

    const orderIds = (userOrders ?? []).map((o) => o.id);

    let hasPurchased = false;
    if (orderIds.length > 0) {
      const { count } = await supabase
        .from("OrderItems")
        .select("id", { count: "exact", head: true })
        .eq("product_id", productId)
        .in("order_id", orderIds);

      hasPurchased = (count ?? 0) > 0;
    }

    if (!hasPurchased) {
      return json(
        { error: "You must purchase this product before writing a review" },
        { status: 403 },
      );
    }

    const { count: reviewCount } = await supabase
      .from("ProductReviews")
      .select("id", { count: "exact", head: true })
      .eq("product_id", productId)
      .eq("user_id", user.id);

    if (reviewCount && reviewCount > 0) {
      return json(
        { error: "You have already reviewed this product" },
        { status: 409 },
      );
    }

    // Insert review
    const { error: reviewError } = await supabase
      .from("ProductReviews")
      .insert({
        product_id: productId,
        user_id: user.id,
        rating,
        title,
        comment: comment || null,
      });

    if (reviewError) {
      console.error("Error adding review:", reviewError);
      return json({ error: "Failed to add review" }, { status: 500 });
    }

    return json({ success: true });
  }

  return json({ error: "Invalid action" }, { status: 400 });
}

export default function ProductDetail() {
  const { product, store, storeAddress, reviews, imageUrls, user } =
    useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const { favorites, addToFavorites, removeFromFavorites } = useFavorites();
  const [promptLogin, setPromptLogin] = useState(false);
  // const [selectedPackaging, setSelectedPackaging] = useState("10lb");
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const { addToCart, canAddStoreProduct } = useCart();
  const canAddThisProduct = canAddStoreProduct(product.store_id);
  const { addToRecentlyViewed } = useRecentlyViewed();

  // Track this product as recently viewed
  const mainImageUrl = useMemo(() => {
    if (imageUrls.length > 0 && !imageUrls[0].includes("placeholder")) {
      return imageUrls[0];
    }
    return product.main_image || null;
  }, [imageUrls, product.main_image]);

  useEffect(() => {
    addToRecentlyViewed({
      id: product.id,
      name: product.name,
      price: product.price,
      markdown: product.markdown,
      mainImageUrl,
      storeId: store?.id || product.store_id,
      storeName: store?.name || null,
    });
  }, [product.id]);

  // Handle successful review submission
  useEffect(() => {
    if (actionData && "success" in actionData) {
      setIsReviewDialogOpen(false);
      setReviewRating(5);
      setReviewComment("");
      // Refresh the page to show the new review
      window.location.reload();
    }
  }, [actionData]);

  // Scroll to top on initial navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleQuantityChange = (change: number) => {
    const newQuantity = Math.max(1, quantity + change);
    setQuantity(newQuantity);
  };

  const getCurrentPrice = () => {
    return product.price || 0;
  };

  const getCurrentDiscount = () => {
    if (product.markdown && product.price) {
      const discount = ((product.price - product.markdown) / product.price) *
        100;
      return `${discount.toFixed(0)}%`;
    }
    return "0%";
  };

  const totalPrice = (getCurrentPrice() * quantity).toFixed(2);

  // Helper for unit label
  const getUnitLabel = (plural = false) => {
    const base = product.per_unit?.toLowerCase() || "unit";
    if (plural) {
      // Simple pluralization: add 's' if not already
      return base.endsWith("s") ? base : base + "s";
    }
    return base;
  };

  const toggleFavorite = async (productId: string) => {
    if (!user) {
      setPromptLogin(true);
      return;
    }

    const isFavorited = favorites.some((fav) => fav.product_id === productId);
    if (isFavorited) {
      removeFromFavorites(productId);
    } else {
      addToFavorites(productId);
    }
  };

  const productJsonLd = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product.name || "Unnamed Product",
    description: product.description || undefined,
    image: imageUrls.filter((u) => !u.includes("placeholder.svg")),
    sku: product.id,
    brand: store?.name ? { "@type": "Brand", name: store.name } : undefined,
    offers: {
      "@type": "Offer",
      url: typeof window !== "undefined" ? window.location.href : undefined,
      priceCurrency: "USD",
      price: (product.markdown ?? product.price ?? 0).toFixed(2),
     availability: (product.stock ?? 0) > 0
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      seller: store?.name
        ? { "@type": "Organization", name: store.name }
        : undefined,
    },
  };

  return (
    <div className="min-h-screen mt-16 bg-background">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productJsonLd) }}
      />
      {/* Login Prompt */}
      {promptLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-lg p-6 w-96">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                Login Required
              </h2>
              <button onClick={() => setPromptLogin(false)}>
                <X className="w-5 h-5 text-gray-600 hover:text-gray-800" />
              </button>
            </div>

            <p className="text-gray-600 mb-6">
              You must be logged in to add items to your favorites.
            </p>

            <div className="flex justify-end gap-3">
              <button
                className="bg-gray-200 px-4 py-2 rounded-md text-sm hover:bg-gray-300"
                onClick={() => setPromptLogin(false)}
              >
                Cancel
              </button>
              <ButtonTheme
                mode="primary"
                onClick={() => (window.location.href = "/login")}
              >
                Log In
              </ButtonTheme>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      {
        /* <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.history.back()}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Search
            </Button>
            <div className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsWishlisted(!isWishlisted)}
            >
              <Heart
                className={`h-4 w-4 mr-2 ${
                  !isWishlisted ? "fill-red-500 text-red-500" : ""
                }`}
              />
              {isWishlisted ? "Saved" : "Save"}
            </Button>
          </div>
        </div>
      </div> */
      }

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="aspect-square rounded-lg overflow-hidden bg-muted">
              <img
                src={imageUrls[selectedImage] || "/placeholder.svg"}
                alt={product.name || "Product"}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex gap-2">
              {imageUrls.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`w-20 h-20 rounded-lg overflow-hidden border-2 ${selectedImage === index ? "border-primary" : "border-muted"
                    }`}
                >
                  <img
                    src={image || "/placeholder.svg"}
                    alt={`${product.name || "Product"} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold">
                  {product.name || "Unnamed Product"}
                </h1>
                <ButtonTheme
                  mode="ghost"
                  size="sm"
                  onClick={() => toggleFavorite(product.id)}
                  title={favorites.some((fav) => fav.product_id === product.id)
                    ? "Remove from favorites"
                    : "Add to favorites"}
                >
                  <Heart
                    className={`w-5 h-5 transition ${
                      favorites.some((fav) => fav.product_id === product.id)
                        ? "fill-brand text-brand"
                        : ""
                    }`}
                    fill={favorites.some((fav) => fav.product_id === product.id)
                      ? "currentColor"
                      : "none"}
                    stroke={favorites.some((fav) =>
                        fav.product_id === product.id
                      )
                      ? "currentColor"
                      : "#6b7280"}
                  />
                </ButtonTheme>
              </div>
              <p className="text-muted-foreground mb-4">
                {product.description || "No description available"}
              </p>

              {/* Supplier Info */}
              {store && (
                <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{store.name}</h3>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                      <div className="flex items-center gap-1">
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        <span>
                          {product.average_rating?.toFixed(1) || "No rating"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        <span>
                          {storeAddress
                            ? `${storeAddress.city}, ${storeAddress.state}`
                            : store.zip != null
                            ? String(store.zip)
                            : "Location not specified"}
                        </span>
                      </div>
                    </div>
                  </div>
                  {store?.id && (
                    <ButtonTheme mode="secondary" size="sm" asChild>
                      <Link to={`/store/${store.slug}/home`}>
                        View Supplier
                      </Link>
                    </ButtonTheme>
                  )}
                </div>
              )}
            </div>

            {/* Pricing */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Pricing & Ordering</span>
                  <div className="text-right">
                    <div className="text-2xl font-bold">
                      ${getCurrentPrice().toFixed(2)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {`per ${getUnitLabel()}`}
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Quantity Selection */}
                <div className="space-y-4">
                  {
                    /* <div>
                    <Label htmlFor="packaging">Package Size</Label>
                    <Select
                      value={selectedPackaging}
                      onValueChange={setSelectedPackaging}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1lb">1 lb package</SelectItem>
                        <SelectItem value="5lb">5 lb package</SelectItem>
                        <SelectItem value="10lb">10 lb package</SelectItem>
                      </SelectContent>
                    </Select>
                  </div> */
                  }

                  <div>
                    <Label htmlFor="quantity">Quantity</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <ButtonTheme
                        mode="secondary"
                        size="sm"
                        onClick={() => handleQuantityChange(-10)}
                        disabled={quantity <= 1}
                      >
                        <Minus className="h-4 w-4" />
                      </ButtonTheme>
                      <Input
                        id="quantity"
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(
                          Math.max(10, Number.parseInt(e.target.value) || 10),
                        )}
                        className="w-24 text-center"
                        min="1"
                      />
                      <ButtonTheme
                        mode="secondary"
                        size="sm"
                        onClick={() => handleQuantityChange(1)}
                      >
                        <Plus className="h-4 w-4" />
                      </ButtonTheme>
                      <span className="text-sm text-muted-foreground">
                        {getUnitLabel(true)}
                      </span>
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span>Unit Price:</span>
                      <span>
                        {`$${
                          getCurrentPrice().toFixed(
                            2,
                          )
                        }/${getUnitLabel()}`}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span>Quantity:</span>
                      <span>
                        {quantity} {getUnitLabel(true)}
                      </span>
                    </div>
                    {getCurrentDiscount() !== "0%" && (
                      <div className="flex justify-between items-center mb-2 text-green-600">
                        <span>Bulk Discount:</span>
                        <span>{getCurrentDiscount()}</span>
                      </div>
                    )}
                    <Separator className="my-2" />
                    <div className="flex justify-between items-center font-bold text-lg">
                      <span>Total:</span>
                      <span>${totalPrice}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <ButtonTheme
                      mode="primary"
                      size="lg"
                      className="flex-1"
                      disabled={!canAddThisProduct}
                      onClick={() => {
                        if (!canAddThisProduct) return;
                        addToCart({
                          id: Date.now().toString(),
                          product_id: product.id,
                          name: product.name || "Unnamed Product",
                          price: getCurrentPrice(),
                          image:
                            mainImageUrl ||
                            product.main_image ||
                            "",
                          quantity: quantity,
                          store_id: product.store_id,
                          store_name: store?.name,
                        });
                      }}
                    >
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Add to Cart
                    </ButtonTheme>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Info */}
            {
              /* <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 text-sm">
                <Truck className="h-4 w-4 text-muted-foreground" />
                <span>1-2 business days</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span>Stock: {product.stock || 0} units</span>
              </div>
            </div> */
            }
          </div>
        </div>

        {/* Product Details Tabs */}
        <Tabs defaultValue="reviews" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="reviews">
              Reviews ({reviews.length})
            </TabsTrigger>
            <TabsTrigger value="shipping">Shipping</TabsTrigger>
          </TabsList>

          <TabsContent value="reviews" className="mt-6">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Customer Reviews</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`h-4 w-4 ${i < Math.floor(product.average_rating || 0)
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-muted-foreground"
                            }`}
                        />
                      ))}
                    </div>
                    <span className="font-medium">
                      {product.average_rating?.toFixed(1) || "No rating"}
                    </span>
                    <span className="text-muted-foreground">
                      ({reviews.length} reviews)
                    </span>
                  </div>
                </div>
                <Dialog
                  open={isReviewDialogOpen}
                  onOpenChange={setIsReviewDialogOpen}
                >
                  <DialogTrigger asChild>
                    <ButtonTheme mode="secondary" disabled={!user}>
                      {user ? "Write Review" : "Sign in to review"}
                    </ButtonTheme>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>Write a Review</DialogTitle>
                    </DialogHeader>
                    <form method="post" className="space-y-4">
                      <input type="hidden" name="action" value="addReview" />

                      <div className="space-y-2">
                        <Label htmlFor="rating">Rating</Label>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setReviewRating(star)}
                              className="p-1"
                            >
                              <Star
                                className={`h-6 w-6 ${star <= reviewRating
                                  ? "fill-yellow-400 text-yellow-400"
                                  : "text-muted-foreground"
                                  }`}
                              />
                            </button>
                          ))}
                        </div>
                        <input
                          type="hidden"
                          name="rating"
                          value={reviewRating}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="comment">Comment (optional)</Label>
                        <Textarea
                          id="comment"
                          name="comment"
                          value={reviewComment}
                          onChange={(e) => setReviewComment(e.target.value)}
                          placeholder="Share your experience with this product..."
                          rows={4}
                        />
                      </div>

                      {actionData && "error" in actionData && (
                        <div className="text-sm text-red-600">
                          {actionData.error}
                        </div>
                      )}

                      <div className="flex gap-2">
                        <ButtonTheme
                          type="submit"
                          mode="primary"
                          disabled={navigation.state === "submitting"}
                          className="flex-1"
                        >
                          {navigation.state === "submitting"
                            ? "Submitting..."
                            : "Submit Review"}
                        </ButtonTheme>
                        <ButtonTheme
                          type="button"
                          mode="secondary"
                          onClick={() => setIsReviewDialogOpen(false)}
                        >
                          Cancel
                        </ButtonTheme>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="space-y-4">
                {reviews.filter(
                    (review) => review.comment && review.comment.trim() !== "",
                  ).length > 0
                  ? (
                    reviews
                      .filter(
                        (review) =>
                          review.comment && review.comment.trim() !== "",
                      )
                      .map((review) => (
                        <Card key={review.id}>
                          <CardContent className="pt-6">
                            <div className="flex items-start justify-between mb-2">
                              <div className="m-0">
                                <Badge variant="secondary" className="text-xs">
                                  Verified Purchase
                                </Badge>
                              </div>
                              <div className="text-right">
                                <div className="flex items-center">
                                  {Array.from({ length: 5 }).map((_, i) => (
                                    <Star
                                      key={i}
                                      className={`h-3 w-3 ${
                                        i < review.rating
                                          ? "fill-yellow-400 text-yellow-400"
                                          : "text-muted-foreground"
                                      }`}
                                    />
                                  ))}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  {new Date(
                                    review.created_at,
                                  ).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                            <p className="text-sm ml-2">{review.comment}</p>
                          </CardContent>
                        </Card>
                      ))
                  )
                  : (
                    <div className="text-center p-8 text-muted-foreground">
                      <Star className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>
                        No reviews with comments yet. Be the first to review
                        this product!
                      </p>
                    </div>
                  )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="shipping" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Delivery Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Minimum Order:
                    </span>
                    <span>
                      {product.minimum_order
                        ? `${product.minimum_order} ${getUnitLabel(true)}`
                        : "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Maximum Order:
                    </span>
                    <span>
                      {product.maximum_order
                        ? `${product.maximum_order} ${getUnitLabel(true)}`
                        : "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Shipping From:
                    </span>
                    <span>
                      {storeAddress
                        ? `${storeAddress.city}, ${storeAddress.state}`
                        : store?.location || "Location not specified"}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Shipping Options</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Pick Up</span>
                    <span className="font-medium">Available</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Standard Delivery
                    </span>
                    <span className="font-medium">Available</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Express Delivery
                    </span>
                    <span className="font-medium">Unavailable</span>
                  </div>
                  {
                    /* <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Refrigerated Transport
                    </span>
                    <span className="font-medium">Unavailable</span>
                  </div> */
                  }
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
